import React, { useState, useMemo, useEffect, useCallback } from 'react'
import { Card } from 'react-bootstrap'
import './ContactCollection.scss'
import { useI18n } from '../../../Utils/_helpers/useI18n'
import { useTimezone } from '../../../hooks/useTimezone'
import {
  TableColumn,
  SearchBar,
  ContactTable,
  ContactPagination,
  ContactMobileCard,
} from './components'
import { useContactExport, useContactAdd, useContactDelete, TotalLeads } from './hooks'
import { useContactData } from './hooks'
import { formatDateWithTimezone } from './utils'
import DeleteConfirmationPopup from '../../../Components/DeleteConfirmationPopup'
import {
  FIELD_FULL_NAME,
  FIELD_SOURCE,
  FIELD_CREATED_AT,
  FIELD_SELECTED,
  FIELD_ACTION,
  FIELD_FLOW,
  FIELD_LAST_UPDATED,
  FIELD_ASSIGN_TO,
  // New fields according to feedback
  FIELD_CARD_OFFERS,
  FIELD_INTENT,
  FIELD_VOICE_NOTES_SUMMARY,
  FIELD_NOTES_PREVIEW,
  FIELD_LAST_REPLY,
  FIELD_TOTAL_MESSAGES_SENT,
  FIELD_SENTIMENT,
  FIELD_OUTBOUND_MESSAGE,
} from './constants'
import { DEFAULT_PAGE, DEFAULT_PAGE_COUNT } from '../../../Utils/Constant/WalletCard'
import { Tag } from '../../../../services/cellcast.v2.types'
import TagPopupModal from '../Contacts/AllContactsList/Modal/TagPopupModal'
import { Contact } from './type'
import { useUpdateRawContactSaleMutation } from '../../../../services/cellcast.v2'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useGetRawContactDetailQuery,
  useUpdateWalletCardPublicMutation,
} from '../../../../services/cellcast.v2'
import { ContactData } from '../../../../services/cellcast.v2.types'
import { toast } from 'react-toastify'
import { ContactEditModal } from './components/ContactEditModal'
import VoiceRecordModal from './VoiceRecordModal'
import { useResponsive } from '../../../hooks/useResponsive'

type ContactCollectionProps = {
  hideStats?: boolean
  embedded?: boolean
  onTotalChange?: (total: TotalLeads) => void
}

export const ContactCollection: React.FC<ContactCollectionProps> = ({
  hideStats = false,
  embedded = false,
  onTotalChange,
}) => {
  // Hooks and refs
  const { t } = useI18n()
  const { userTimezone } = useTimezone()
  const location = useLocation()
  const navigate = useNavigate()

  const { isMobile } = useResponsive()

  const params = new URLSearchParams(location.search)
  const initialFilter = params.get('filter') || 'all'

  // Custom hooks
  const {
    contacts,
    total,
    page,
    searchTerm,
    setSearchTerm,
    filterValue,
    setFilterValue,
    sortKey,
    sortDirection,
    isLoading,
    clearSearch,
    handleSort,
    handlePageChange,
    fetchContacts,
  } = useContactData(initialFilter)

  const { handleExport, isExporting } = useContactExport(
    searchTerm,
    sortKey,
    sortDirection,
    page.size
  )

  // Add and delete contact hooks with callback to refresh data
  const { handleAddContact, isAdding } = useContactAdd(() => fetchContacts({ forceRefresh: true }))
  const { handleDeleteContacts, isDeleting } = useContactDelete()
  const [updateRawContactStatus] = useUpdateRawContactSaleMutation()

  // Selection state
  const [selectedContacts, setSelectedContacts] = useState<string[]>([])
  const [selectedAll, setSelectedAll] = useState(false)
  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [openTagPopup, setOpenTagPopup] = useState<boolean>(false)
  // Delete confirmation state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editContact, setEditContact] = useState<Contact | null>(null)
  const [contactId, setContactId] = useState<string | null>(null)
  const [updateWalletCardPublic] = useUpdateWalletCardPublicMutation()
  const [voiceModalContact, setVoiceModalContact] = useState<Contact | null>(null)
  const [showVoiceModal, setShowVoiceModal] = useState(false)

  // Get contact detail by ID
  const { data: contactDetail } = useGetRawContactDetailQuery({ id: contactId! }, { skip: !contactId })

  // Handle URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search)

    // Handle id parameter for edit modal
    const idParam = params.get('id')
    if (idParam) {
      // Always fetch fresh data from API when opening edit modal
      setContactId(idParam)
    }
    // Handle filter parameter
    const filterParam = params.get('filter') || 'all'
    if (filterParam !== filterValue) {
      setFilterValue(filterParam)
    }
  }, [location.search, filterValue, setFilterValue])

  // Handle contact detail from API
  useEffect(() => {
    if (contactDetail?.data) {
      setEditContact(contactDetail.data as unknown as Contact)
      setEditModalOpen(true)
      setContactId(null) // Reset to prevent refetch
    }
  }, [contactDetail])

  const handleCloseEditModal = () => {
    setEditModalOpen(false)
    setEditContact(null)
    setContactId(null)
    const params = new URLSearchParams(location.search)
    params.delete('id')
    // Keep filter parameter when closing modal
    navigate({ pathname: location.pathname, search: params.toString() })
  }

  const handleUpdateContact = async (updated: Partial<ContactData>) => {
    try {
      const response: any = await updateWalletCardPublic(updated as ContactData)
      if (response?.data) {
        toast.success('Contact updated successfully')
        fetchContacts({ forceRefresh: true })
      } else {
        toast.error('Failed to update contact')
      }
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update contact')
    }

    handleCloseEditModal()
  }

  // Table configuration
  const LOCAL_STORAGE_KEY = 'contactCollectionColumnOrder'

  const initialColumns: TableColumn[] = [
    // Checkbox column remains first (not part of user order but needed for selection)
    { key: FIELD_SELECTED, title: '', width: '30px', className: 'checkbox-column' },

    // Column order according to feedback image:
    // 1. Flow Name (Add icon to indicate if; Active (in-flow), Paused (in-flow))
    {
      key: FIELD_FLOW,
      title: t('RAW_CONTACT_COLLECTION.FLOW'),
      width: '120px',
      iconPath: '/media/icons/lead-management/th-flow.svg',
    },

    // 2. Name
    { key: FIELD_FULL_NAME, title: t('RAW_CONTACT_COLLECTION.NAME'), width: '150px' },

    // 3. Card Offers(Selected card offers)
    { key: FIELD_CARD_OFFERS, title: t('RAW_CONTACT_COLLECTION.CARD_OFFERS'), width: '120px' },

    // 4. NEW Column - Intent (Hot (confirm the states))
    { key: FIELD_INTENT, title: t('RAW_CONTACT_COLLECTION.INTENT'), width: '100px' },

    // 5. NEW Column - Sentiment (Confirm the states) (Add as tag in BE)
    { key: FIELD_SENTIMENT, title: t('RAW_CONTACT_COLLECTION.SENTIMENT'), width: '100px' },

    // 6. NEW Column - Voice Notes Summary. (Clickable to add notes)
    {
      key: FIELD_VOICE_NOTES_SUMMARY,
      title: t('RAW_CONTACT_COLLECTION.VOICE_NOTES_SUMMARY'),
      width: '150px',
    },

    // 7. Notes Preview (Text). (Clickable to add notes)
    { key: FIELD_NOTES_PREVIEW, title: t('RAW_CONTACT_COLLECTION.NOTES_PREVIEW'), width: '150px' },

    // 8. Last Outbound Message (Show Date + x days from message)
    {
      key: FIELD_OUTBOUND_MESSAGE,
      title: t('RAW_CONTACT_COLLECTION.LAST_OUTBOUND_MESSAGE'),
      width: '150px',
      iconPath: '/media/icons/lead-management/th-out-bound-mess.svg',
    },

    // 9. Last Reply (Date) + x days from message)
    { key: FIELD_LAST_REPLY, title: t('RAW_CONTACT_COLLECTION.LAST_REPLY'), width: '120px' },

    // 10. Total Messages sent (xx)
    {
      key: FIELD_TOTAL_MESSAGES_SENT,
      title: t('RAW_CONTACT_COLLECTION.TOTAL_MESSAGES_SENT'),
      width: '120px',
    },

    // 11. Created (Date entered into system)
    {
      key: FIELD_CREATED_AT,
      title: t('RAW_CONTACT_COLLECTION.CREATED'),
      width: '120px',
      iconPath: '/media/icons/lead-management/th-date-create.svg',
      render: (value: string) =>
        formatDateWithTimezone(value, 'MMMM DD, YYYY hh:mm A', userTimezone),
    },

    // 12. Last Action (Message, Call) (Date + Time last action)
    {
      key: FIELD_LAST_UPDATED,
      title: t('RAW_CONTACT_COLLECTION.LAST_ACTION'),
      width: '150px',
      iconPath: '/media/icons/lead-management/th-last-update.svg',
      render: (value: string) =>
        formatDateWithTimezone(value, 'MMMM DD, YYYY hh:mm A', userTimezone),
    },

    // 13. Source (B-card, Lead form, LiveChat, Manually added, API)
    { key: FIELD_SOURCE, title: t('RAW_CONTACT_COLLECTION.SOURCE'), width: '100px' },

    // 14. Assigned to
    { key: FIELD_ASSIGN_TO, title: t('RAW_CONTACT_COLLECTION.ASSIGN_TO'), width: '100px' },

    // Action column stays last
    { key: FIELD_ACTION, title: t('RAW_CONTACT_COLLECTION.ACTION'), width: '100px' },

    // Comment out unused columns that are not in feedback
    // {key: FIELD_STATUS, title: t('RAW_CONTACT_COLLECTION.STATUS'), width: '100px'},
    // {key: FIELD_SALES, title: t('RAW_CONTACT_COLLECTION.SALES'), width: '120px'},
    // {key: FIELD_TAGS, title: t('RAW_CONTACT_COLLECTION.TAGS'), width: '100px'},
    // {key: FIELD_PHONE, title: t('RAW_CONTACT_COLLECTION.NUMBER'), width: '150px', iconPath: '/media/icons/lead-management/th-number.svg'},
    // {key: FIELD_EMAIL, title: t('RAW_CONTACT_COLLECTION.EMAIL'), width: '200px'},
    // {key: FIELD_NOTES, title: t('RAW_CONTACT_COLLECTION.NOTES'), width: '100px', iconPath: '/media/icons/lead-management/th-note.svg'},
    // {key: FIELD_PROMO_ACTION, title: t('RAW_CONTACT_COLLECTION.PROMO_ACTION'), width: '100px', iconPath: '/media/icons/lead-management/th-promo-action.svg'},
    // {key: FIELD_REPLIES, title: t('RAW_CONTACT_COLLECTION.REPLIES'), width: '100px', iconPath: '/media/icons/lead-management/th-replies.svg'},
    // {key: FIELD_LAST_MESSAGE_PREVIEW, title: t('RAW_CONTACT_COLLECTION.LAST_MESSAGE_PREVIEW'), width: '200px'},
    // {key: FIELD_STATUS_SUBSCRIBE, title: t('RAW_CONTACT_COLLECTION.STATUS_SUBSCRIBE'), width: '80px'},
    // {key: FIELD_UNSUBSCRIBED_AT, title: t('RAW_CONTACT_COLLECTION.UNSUBSCRIBED_AT'), width: '80px'},
  ]

  // Apply saved column order only if it is fully compatible with current columns
  const applySavedOrder = (cols: TableColumn[]): TableColumn[] => {
    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || 'null') as string[] | null

      // If no saved data -> use default order
      if (!saved || !Array.isArray(saved)) return cols

      // Check compatibility: same length and every key exists in current columns
      const isCompatible =
        saved.length === cols.length && saved.every((key) => cols.find((c) => c.key === key))

      if (!isCompatible) {
        // Incompatible -> clear saved value and use default order
        localStorage.removeItem(LOCAL_STORAGE_KEY)
        return cols
      }

      const map: Record<string, TableColumn> = {}
      cols.forEach((c) => {
        map[c.key] = c
      })

      // Build ordered columns based on saved keys order
      const ordered: TableColumn[] = saved.map((k) => map[k])
      return ordered
    } catch (_) {
      return cols
    }
  }

  const [tableColumns, setTableColumns] = useState<TableColumn[]>(applySavedOrder(initialColumns))

  const FIXED_KEYS = [FIELD_SELECTED, FIELD_ACTION]
  const handleColumnReorder = (sourceIndex: number, destinationIndex: number) => {
    if (
      FIXED_KEYS.includes(tableColumns[sourceIndex].key) ||
      FIXED_KEYS.includes(tableColumns[destinationIndex].key)
    ) {
      return tableColumns // ignore move
    }
    setTableColumns((prev) => {
      const updated = [...prev]
      const [removed] = updated.splice(sourceIndex, 1)
      updated.splice(destinationIndex, 0, removed)
      const keys = updated.map((c) => c.key)
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(keys))
      return updated
    })
  }

  // Notify parent of total changes
  useEffect(() => {
    if (onTotalChange) {
      onTotalChange(total)
    }
  }, [total, onTotalChange])

  // UI helpers
  const tableMinHeight = contacts.length > 0 ? `${Math.min(contacts.length * 50, 300)}px` : '200px'

  const pageCount = Math.ceil(total?.total / page.size)

  // Calculate valid leads (contacts excluding unverified status)
  const validLeads = total?.countValid || 0

  const summaryText = `${validLeads} Valid Leads / ${total?.total} Total Entries`

  // Derived selection info
  const selectedObjects = useMemo(
    () => contacts.filter((c) => selectedContacts.includes(c.id)),
    [contacts, selectedContacts]
  )
  const verifiedSelectedCount = selectedObjects.filter((c) => c.status === 'verified').length
  const canAddContact = selectedObjects.length > 0 && verifiedSelectedCount === 0

  // Event handlers
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleFilterChange = (value: string) => {
    // Update URL with new filter
    const params = new URLSearchParams(location.search)
    if (value === 'all') {
      params.delete('filter')
    } else {
      params.set('filter', value)
    }
    navigate({ pathname: location.pathname, search: params.toString() })
    // Fetch contacts with new filter
    fetchContacts({ newFilter: value, forceRefresh: true })
  }

  // Contact selection handlers
  const handleSelectContact = (id: string) => {
    setSelectedContacts((prevSelected) => {
      if (prevSelected.includes(id)) {
        // If already selected, remove it
        const newSelected = prevSelected.filter((contactId) => contactId !== id)
        setSelectedAll(false)
        return newSelected
      } else {
        const newSelected = [...prevSelected, id]
        if (contacts.every((contact) => newSelected.includes(contact.id))) {
          setSelectedAll(true)
        }
        return newSelected
      }
    })
  }

  const handleSelectAll = () => {
    setSelectedAll((prevSelectedAll) => {
      if (prevSelectedAll) {
        // Deselect all
        setSelectedContacts([])
        return false
      } else {
        // Select all contacts on current page
        setSelectedContacts(contacts.map((contact) => contact.id))
        return true
      }
    })
  }

  // Action handlers
  const handleAddContactAction = () => {
    // Ensure we add only IDs that are currently visible/selected on this page
    const idsToAdd = selectedObjects.map((c) => c.id)
    if (idsToAdd.length === 0) {
      return
    }

    handleAddContact(idsToAdd).then(() => {
      // Clear selection after successful addition
      setSelectedContacts([])
      setSelectedAll(false)
      // Refresh data
      fetchContacts({ forceRefresh: true })
    })
  }

  const handleDeleteContactsAction = () => {
    // Show delete confirmation popup
    setShowDeleteConfirmation(true)
  }

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(async () => {
    if (selectedContacts.length > 0) {
      try {
        // Call the delete hook with the selected IDs
        const success = await handleDeleteContacts(selectedContacts)

        if (success) {
          // Reset selection state
          setSelectedContacts([])
          setSelectedAll(false)

          // Calculate if we need to go to previous page
          const remainingItemsOnCurrentPage = contacts.length - selectedContacts.length
          const shouldGoToPreviousPage = remainingItemsOnCurrentPage === 0 && page.page > 1

          if (shouldGoToPreviousPage) {
            // First update the page number
            const newPage = page.page - 1
            await handlePageChange(newPage)

            // Then force a data refresh for the new page
            await fetchContacts({
              forceRefresh: true,
              newPage: newPage,
            })
          } else {
            // Stay on current page but refresh data
            await fetchContacts({
              forceRefresh: true,
              newPage: page.page,
            })
          }
        }
      } catch (error) {
        console.error('Error during delete operation:', error)
      }
    }

    // Close the confirmation dialog
    setShowDeleteConfirmation(false)
  }, [
    selectedContacts,
    handleDeleteContacts,
    contacts.length,
    page.page,
    handlePageChange,
    fetchContacts,
  ])

  const deleteConfirmationMessage = useMemo(() => {
    const name = selectedContacts.length === 1 ? 'contact' : 'contacts'
    return `${t('COMMON.ACTIONS.DELETE_CONFIRMATION', {
      name: `${selectedContacts.length} selected ${name}`,
    })}`
  }, [t, selectedContacts.length])

  const handleCloseTagPopup = () => {
    setOpenTagPopup(false)
  }

  // Delete confirmation popup
  const deleteConfirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={showDeleteConfirmation}
        handleClose={() => setShowDeleteConfirmation(false)}
        Message={deleteConfirmationMessage}
        confirm={handleDeleteConfirm}
      />
    ),
    [showDeleteConfirmation, deleteConfirmationMessage, handleDeleteConfirm]
  )

  // Reset selection when page changes
  React.useEffect(() => {
    setSelectedContacts([])
    setSelectedAll(false)
  }, [page.page])

  // Toggle verification status for a single contact
  const handleChangeSaleStatus = async (contact: Contact, newStatus: boolean) => {
    try {
      await updateRawContactStatus({ id: contact.id, sales: newStatus }).unwrap()
    } finally {
      fetchContacts({ forceRefresh: true })
    }
  }

  const handleToggleSale = (contact: Contact, newStatus: boolean) => {
    handleChangeSaleStatus(contact, newStatus)
    fetchContacts({ forceRefresh: true })
  }

  const content = (
    <>
      {deleteConfirmation}
      <TagPopupModal
        show={openTagPopup}
        handleClose={handleCloseTagPopup}
        setSelectedTag={setSelectedTags}
        selectedTag={selectedTags}
        refreshDataCallback={() => fetchContacts({ forceRefresh: true })}
      />

      {!hideStats && <StatsLeadManagement total={total} isMobile={isMobile} />}
      <Card className=''>
        <Card.Body>
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onClear={clearSearch}
            isLoading={isLoading || isDeleting || isAdding}
            placeholder={t('RAW_CONTACT_COLLECTION.SEARCH_PLACEHOLDER')}
            selectedContacts={selectedContacts}
            onAddContact={handleAddContactAction}
            onDeleteContacts={handleDeleteContactsAction}
            canAddContact={canAddContact}
            verifiedSelectedCount={verifiedSelectedCount}
            fetchContacts={fetchContacts}
            onExport={handleExport}
            isExporting={isExporting}
            filterValue={filterValue}
            onFilterChange={handleFilterChange}
          />

          {/* Mobile view: Card layout */}
          {isMobile ? (
            <div className='mobile-contacts-list'>
              {isLoading || isDeleting || isAdding ? (
                <div className='text-center py-5'>
                  <div className='spinner-border text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : contacts.length === 0 ? (
                <div className='text-center py-5 text-muted'>
                  {t('RAW_CONTACT_COLLECTION.NO_CONTACTS_FOUND')}
                </div>
              ) : (
                contacts?.map((contact) => (
                  <ContactMobileCard
                    key={contact.id}
                    contact={contact}
                    isSelected={selectedContacts.includes(contact.id)}
                    onSelect={handleSelectContact}
                    onDelete={() => {
                      setSelectedContacts([contact.id])
                      handleDeleteContactsAction()
                    }}
                    onToggleSale={handleToggleSale}
                    actions={[
                      {
                        key: 'call',
                        label: t('RAW_CONTACT_COLLECTION.CALL') || 'Call',
                        icon: 'bi bi-telephone',
                        onClick: () => {
                          const phone = String(contact.phone || '').replace(/\D/g, '')
                          if (!phone) return
                          window.open(`tel:${contact.countryCode + phone}`, '_self')
                          try {
                            ; (handleUpdateContact as any)({
                              id: contact.id,
                              callLastTime: new Date().toISOString(),
                            })
                          } catch (_) { }
                        },
                      },
                      {
                        key: 'msg',
                        label: t('RAW_CONTACT_COLLECTION.SEND_QUICK_MESSAGE') || 'Message',
                        icon: 'bi bi-send',
                        onClick: () => {
                          const phone = String(contact.phone || '').replace(/\D/g, '')
                          if (!phone) return
                          navigate({ pathname: '/inbox', search: `?receiver=${phone}` })
                        },
                      },
                      {
                        key: 'voice',
                        label: t('RAW_CONTACT_COLLECTION.START_RECORDING') || 'Voice note',
                        icon: 'bi bi-mic-fill',
                        onClick: () => {
                          setVoiceModalContact(contact as any)
                          setShowVoiceModal(true)
                        },
                      },
                      {
                        key: 'edit',
                        label: t('RAW_CONTACT_COLLECTION.EDIT_PROFILE') || 'Edit profile',
                        icon: 'bi bi-pencil',
                        onClick: () => {
                          console.log('contact', contact)
                          setEditContact(contact as any)
                          setEditModalOpen(true)
                        },
                      },
                      {
                        key: 'voiceNotes',
                        label:
                          t('RAW_CONTACT_COLLECTION.VIEW_VOICE_AI_NOTES') || 'View Voice AI Notes',
                        icon: 'bi bi-music-note-list',
                        onClick: () => navigate(`/record-list/${contact.id}`),
                      },
                    ]}
                  />
                ))
              )}
            </div>
          ) : (
            /* Desktop view: Table layout */
            <ContactTable
              contacts={contacts}
              columns={tableColumns}
              isLoading={isLoading || isDeleting || isAdding}
              noDataMessage={t('RAW_CONTACT_COLLECTION.NO_CONTACTS_FOUND')}
              minHeight={tableMinHeight}
              sortKey={sortKey}
              sortDirection={sortDirection}
              onSort={handleSort as (key: string | keyof Contact) => void}
              selectedContacts={selectedContacts}
              onSelectContact={handleSelectContact}
              selectedAll={selectedAll}
              onSelectAll={handleSelectAll}
              onDeleteContacts={handleDeleteContactsAction}
              setSelectedContacts={setSelectedContacts}
              onContactUpdated={() => fetchContacts({ forceRefresh: true })}
              onColumnReorder={handleColumnReorder}
              onToggleSale={handleToggleSale}
              scrollOnTop={true}
            />
          )}

          <ContactPagination
            page={page.page || DEFAULT_PAGE}
            pageCount={pageCount || DEFAULT_PAGE_COUNT}
            total={total?.total}
            onPageChange={handlePageChange}
            isLoading={isLoading || isDeleting || isAdding}
            totalLabel={t('RAW_CONTACT_COLLECTION.TOTAL_DATA')}
            summaryText={summaryText}
          />
        </Card.Body>
      </Card>
      <VoiceRecordModal
        show={showVoiceModal}
        contact={voiceModalContact}
        onClose={() => setShowVoiceModal(false)}
        onSaved={() => {
            navigate(`/record-list/${voiceModalContact?.id}`)
            setShowVoiceModal(false)
        }}
      />
      {editContact && (
        <ContactEditModal
          show={editModalOpen}
          contact={editContact}
          onClose={handleCloseEditModal}
          onUpdate={handleUpdateContact}
          onVoiceSaved={() => fetchContacts({ forceRefresh: true })}
        />
      )}
    </>
  )

  if (embedded) {
    return content
  }

  return (
    <div className='contact-collection-container' data-page="contact-collection">
      {content}
    </div>
  )
}

export const StatsLeadManagement = ({
  total,
  isMobile,
}: {
  total: TotalLeads
  isMobile: boolean
}) => {
  return (
    <div className={`row mb-8 stats-cards-row ${isMobile ? 'g-4' : 'g-8'}`}>
      <div className='col'>
        <div className='matrix-card'>
          <div className='d-flex flex-column w-100'>
            <h1 className='fw-bolder fs-1 mb-1'>{total?.totalWarmUp}</h1>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span className='text-muted fw-bold fs-6'>Warming Up</span>
            </div>
            <span className='text-success fw-bold fs-7'>
              {total?.totalWarmUpGrowth}% from last month
            </span>
          </div>
        </div>
      </div>
      <div className='col'>
        <div className='matrix-card'>
          <div className='d-flex flex-column w-100'>
            <h1 className='fw-bolder fs-1 mb-1'>{(total?.totalBites || 0)}</h1>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span className='text-muted fw-bold fs-6'>Bites</span>
            </div>
            <span className='text-success fw-bold fs-7'>
              {(total as any)?.totalBitesGrowth}% from last month
            </span>
          </div>
        </div>
      </div>
      <div className='col'>
        <div className='matrix-card'>
          <div className='d-flex flex-column w-100'>
            <h1 className='fw-bolder fs-1 mb-1'>{(total?.totalSales || 0)}</h1>
            <div className='d-flex justify-content-between align-items-center mb-2'>
              <span className='text-muted fw-bold fs-6'>Sold</span>
            </div>
            <span className='text-success fw-bold fs-7'>
              {(total as any)?.totalSalesGrowth}% from last month
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
